import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError, getUserSettings, refreshTicket, reuseTicket } from '@/api';
import { countBets, type BetslipTicket } from '@/domain/betslip';
import { betslipReplace } from './betslip-store';

export interface ReuseBetSuccess {
  selectionCount: number;
}

export interface UseReuseBetOptions {
  onSuccess?: (result: ReuseBetSuccess) => void;
}

function normalizeReusedTicket(ticket: BetslipTicket, userGuid: string | undefined): BetslipTicket {
  const items = (ticket.Items ?? []).map((item) => {
    const eventDate = item.Event?.Date;
    const isPast =
      typeof eventDate === 'string' &&
      eventDate.length > 0 &&
      !Number.isNaN(Date.parse(eventDate)) &&
      Date.parse(eventDate) < Date.now();
    if (!isPast) return item;
    return {
      ...item,
      Event: { ...item.Event, Live: true },
    };
  });

  return {
    ...ticket,
    Items: items,
    User: userGuid ?? ticket.User,
    Changed: false,
  };
}

/**
 * Reuse → refresh → replace betslip store (legacy `ReuseBet` without cashout).
 */
export function useReuseBet(options?: UseReuseBetOptions) {
  const { t } = useTranslation();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async (ticketId: string): Promise<ReuseBetSuccess> => {
      setErrorMessage(null);
      const user = getUserSettings();
      let reused = await reuseTicket(ticketId);
      reused = normalizeReusedTicket(reused, user?.Guid);

      const customSnapshots: {
        itemIndex: number;
        betIndex: number;
        selections: unknown;
        odd: BetslipTicket['Items'][number]['Bets'][number]['Odd'];
      }[] = [];
      for (let i = 0; i < reused.Items.length; i += 1) {
        const bets = reused.Items[i]?.Bets ?? [];
        for (let j = 0; j < bets.length; j += 1) {
          const bet = bets[j];
          if (bet?.Custom) {
            customSnapshots.push({
              itemIndex: i,
              betIndex: j,
              selections: (bet as { Selections?: unknown }).Selections,
              odd: bet.Odd,
            });
          }
        }
      }

      let refreshed = await refreshTicket(reused);
      for (const snap of customSnapshots) {
        const item = refreshed.Items[snap.itemIndex];
        const bet = item?.Bets[snap.betIndex];
        if (!bet) continue;
        if (!bet.Custom) {
          bet.Custom = true;
          (bet as { Selections?: unknown }).Selections = snap.selections;
          bet.Odd = snap.odd;
        }
      }

      refreshed = normalizeReusedTicket(refreshed, user?.Guid);
      const selectionCount = countBets(refreshed);
      if (selectionCount === 0 || refreshed.Items.length === 0) {
        throw new Error(
          t('NoAvailableBetsFromThisTicket', {
            defaultValue: 'No available bets from this ticket',
          }),
        );
      }

      betslipReplace(refreshed);
      return { selectionCount };
    },
    onSuccess: (result) => {
      options?.onSuccess?.(result);
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        setErrorMessage(error.message || t('Error', { defaultValue: 'Error' }));
        return;
      }
      setErrorMessage(
        error instanceof Error ? error.message : t('Error', { defaultValue: 'Error' }),
      );
    },
  });

  return {
    reuseBet: mutation.mutateAsync,
    isPending: mutation.isPending,
    errorMessage,
    clearError: () => setErrorMessage(null),
    pendingTicketId: mutation.isPending ? (mutation.variables ?? null) : null,
  };
}
